/**
 * e-Services (Tier 5.2) API layer. The Django API wraps bodies in the standard
 * envelope (`{ success, message, data }`); this layer unwraps `data` and maps
 * snake_case↔camelCase. The e-Services password is sent write-only and never
 * stored anywhere client-side.
 */

import { apiFetch, apiFetchResponse } from "@/lib/api";
import type {
  AddMonitorInput,
  AddRequestInput,
  CafMonitorRow,
  EServicesStatus,
  MailboxSummaryRow,
  OpenSessionResult,
  PoaPendingRow,
  TranscriptMonitor,
  TranscriptRequest,
} from "./types";

interface Envelope<T> {
  data: T;
}

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

const toCamel = (s: string) => s.replace(/_([a-z0-9])/g, (_, c) => c.toUpperCase());
const toSnake = (s: string) => s.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);

function rename<T>(value: unknown, fn: (k: string) => string): T {
  if (Array.isArray(value)) return value.map((v) => rename(v, fn)) as T;
  if (isPlainObject(value)) {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) out[fn(k)] = rename(v, fn);
    return out as T;
  }
  return value as T;
}

const camelize = <T>(v: unknown): T => rename<T>(v, toCamel);
const decamelize = (v: unknown): unknown => rename(v, toSnake);

// --- hub / status --------------------------------------------------------

export async function getStatus(): Promise<EServicesStatus> {
  const { data } = await apiFetch<Envelope<unknown>>("/eservices/status/");
  return camelize<EServicesStatus>(data);
}

export async function setEsMonitor(value: boolean): Promise<void> {
  await apiFetch("/eservices/status/", {
    method: "PATCH",
    json: { es_monitor: value },
  });
}

export async function openSession(input: {
  practitioner: string;
  esUsername: string;
  esPassword: string;
  rememberMonitor?: boolean;
}): Promise<OpenSessionResult> {
  const { data } = await apiFetch<Envelope<unknown>>("/eservices/session/", {
    method: "POST",
    json: decamelize(input),
  });
  return camelize<OpenSessionResult>(data);
}

export async function selectOrg(practitioner: string, org: string): Promise<void> {
  await apiFetch("/eservices/session/select-org/", {
    method: "POST",
    json: { practitioner, org },
  });
}

export async function testSession(
  practitioner: string,
): Promise<{ ok: boolean; problems: string[] }> {
  const { data } = await apiFetch<Envelope<{ ok: boolean; problems: string[] }>>(
    "/eservices/session/test/",
    { method: "POST", json: { practitioner } },
  );
  return data;
}

export async function clearCredentials(practitioner: string): Promise<void> {
  await apiFetch(`/eservices/session/${practitioner}/credentials/`, {
    method: "DELETE",
  });
}

// --- CAF monitor report --------------------------------------------------

export async function getCafMonitor(ordering?: string): Promise<CafMonitorRow[]> {
  const q = ordering ? `?ordering=${encodeURIComponent(ordering)}` : "";
  const { data } = await apiFetch<Envelope<{ rows: unknown[] }>>(
    `/eservices/caf-monitor/${q}`,
  );
  return camelize<CafMonitorRow[]>(data.rows);
}

export async function downloadCafCsv(): Promise<Blob> {
  const res = await apiFetchResponse("/eservices/caf-monitor/?export=csv");
  return res.blob();
}

// --- POA -----------------------------------------------------------------

export async function getPendingPoas(firm?: string): Promise<PoaPendingRow[]> {
  const qs = firm ? `?firm=${encodeURIComponent(firm)}` : "";
  const { data } = await apiFetch<Envelope<{ rows: unknown[] }>>(
    `/eservices/poa/pending/${qs}`,
  );
  return camelize<PoaPendingRow[]>(data.rows);
}

export async function dismissPoas(documentIds: string[]): Promise<void> {
  await apiFetch("/eservices/poa/dismiss/", {
    method: "POST",
    json: { document_ids: documentIds },
  });
}

export async function promotePoa(
  docId: string,
  input: { form: string; practitioner?: string; yearFrom?: number; yearTo?: number; autoCheck: boolean },
): Promise<void> {
  await apiFetch(`/eservices/poa/${docId}/monitor/`, {
    method: "POST",
    json: decamelize(input),
  });
}

// --- secure mailbox ------------------------------------------------------

export async function getMailbox(): Promise<{
  lastCheck: string | null;
  summary: MailboxSummaryRow[];
}> {
  const { data } = await apiFetch<Envelope<unknown>>("/eservices/mailbox/");
  return camelize(data);
}

export async function checkMailbox(): Promise<void> {
  await apiFetch("/eservices/mailbox/check/", { method: "POST" });
}

// --- client-nested: transcript requests ----------------------------------

export async function listRequests(
  clientId: string,
  forWho?: string,
): Promise<TranscriptRequest[]> {
  const q = forWho ? `?for_who=${forWho}` : "";
  const { data } = await apiFetch<Envelope<unknown[]>>(
    `/clients/${clientId}/transcript-requests/${q}`,
  );
  return camelize<TranscriptRequest[]>(data);
}

export async function addRequests(
  clientId: string,
  input: AddRequestInput,
): Promise<{ created: number; skipped: number }> {
  const { data } = await apiFetch<Envelope<{ created: number; skipped: number }>>(
    `/clients/${clientId}/transcript-requests/`,
    { method: "POST", json: decamelize(input) },
  );
  return data;
}

export async function requeueRequests(clientId: string, ids: string[]): Promise<void> {
  await apiFetch(`/clients/${clientId}/transcript-requests/requeue/`, {
    method: "POST",
    json: { ids },
  });
}

export async function deleteRequests(clientId: string, ids: string[]): Promise<void> {
  await apiFetch(`/clients/${clientId}/transcript-requests/bulk-delete/`, {
    method: "POST",
    json: { ids },
  });
}

// --- client-nested: transcript monitors -----------------------------------

export async function listMonitors(
  clientId: string,
): Promise<TranscriptMonitor[]> {
  const { data } = await apiFetch<Envelope<unknown[]>>(
    `/clients/${clientId}/transcript-monitors/`,
  );
  return camelize<TranscriptMonitor[]>(data);
}

export async function addMonitor(
  clientId: string,
  input: AddMonitorInput,
): Promise<TranscriptMonitor> {
  const { data } = await apiFetch<Envelope<unknown>>(
    `/clients/${clientId}/transcript-monitors/`,
    { method: "POST", json: decamelize(input) },
  );
  return camelize<TranscriptMonitor>(data);
}

export async function updateMonitor(
  clientId: string,
  id: string,
  input: Partial<AddMonitorInput>,
): Promise<TranscriptMonitor> {
  const { data } = await apiFetch<Envelope<unknown>>(
    `/clients/${clientId}/transcript-monitors/${id}/`,
    { method: "PATCH", json: decamelize(input) },
  );
  return camelize<TranscriptMonitor>(data);
}

export async function deleteMonitor(clientId: string, id: string): Promise<void> {
  await apiFetch(`/clients/${clientId}/transcript-monitors/${id}/`, {
    method: "DELETE",
  });
}
